import type { AlertProps as MuiAlertProps } from '@mui/material/Alert';
import MuiAlert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { CheckCircle, Info, Warning, WarningCircle, X } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import { IconButton } from '../Button';
import { AlertTitle } from './AlertTitle';

/** Brand severities. Figma's `severity=info` renders in brand purple (not MUI's denim `palette.info`). */
export type AlertSeverity = 'info' | 'success' | 'warning' | 'error';

/**
 * Per-severity status icons (Phosphor), mirroring MUI's severity→icon convention with the
 * brand icon set. Applied as the default `iconMapping`; override per-instance via `iconMapping`
 * or drop the icon with `icon={false}`.
 */
const DEFAULT_ICON_MAPPING: Record<AlertSeverity, ReactNode> = {
  info: <Info />,
  success: <CheckCircle />,
  warning: <Warning />,
  error: <WarningCircle />,
};

export interface AlertProps extends Omit<MuiAlertProps, 'title' | 'severity' | 'variant'> {
  /** Status of the message — drives the accent colour and status icon. */
  severity?: AlertSeverity;
  /** Bold lead-in (rendered inside `AlertTitle`, body1Bold). */
  title?: ReactNode;
  /** A single call-to-action (pass a brand `Button variant="text" size="small"`). */
  action?: ReactNode;
  /**
   * Where the call-to-action sits. `right` (default) places it in its own column, vertically
   * centred against the message and left of the close ✕; `bottom` places it under the
   * description, inside the message box. The two placements are mutually exclusive — an Alert
   * shows the CTA in one spot, never both.
   */
  actionPlacement?: 'right' | 'bottom';
}

/** Would this node paint anything? Keeps the description wrapper (and its gap) out of the DOM
 *  when there is no description — `children: undefined` is the title-only Alert. */
function isRenderable(node: ReactNode): boolean {
  return node !== undefined && node !== null && node !== false && node !== '';
}

/**
 * Alert — a brief, non-interrupting message about the state of a task or view.
 *
 * Thin wrapper over `@mui/material/Alert`. Lays out the brand anatomy (Figma node 1636:6992) as
 * a row of columns: a per-severity status icon (its own top-aligned column), a message box
 * (`title` + optional `description` = `children` + an optional bottom CTA), an optional
 * right-placed CTA (`action`), and an optional close button (rendered when `onClose` is set,
 * pinned to the top of its own full-height column). Drop the icon with `icon={false}` and the
 * whole message shifts left into the freed column.
 *
 * The brand appearance is a single "standard" style — neutral surface with a coloured left
 * accent — applied in the theme (`MuiAlert` styleOverrides), not here. MUI's `filled`/`outlined`
 * variants are intentionally not exposed.
 *
 * Sub-components: `AlertTitle` (LOCAL). SHARED primitives: `Paper` (Alert root; also
 * Card/Dialog/Menu/Drawer/Popover/Accordion) and `IconButton` (close button; also
 * Button/ListItemButton/etc.) — neither is themed globally from here; styling is scoped to
 * the `MuiAlert*` slots.
 */
export const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  {
    severity = 'info',
    title,
    action,
    actionPlacement = 'right',
    onClose,
    closeText = 'Close',
    iconMapping,
    children,
    slotProps,
    ...rest
  },
  ref
) {
  // Icon alignment is line-count aware (designer request): with a SINGLE line of text the status
  // icon (and the ✕ / right CTA) are vertically centred; with MORE than one line — title +
  // description, or a description that wraps — the icon aligns to the first line (top). CSS can't
  // count wrapped lines, so we measure the message box: it's "single line" when its rendered
  // height is within ~1.5 line-heights. The result is exposed as `data-single-line` on the root,
  // and the actual alignment switch lives in the theme (`MuiAlert` styleOverrides).
  const messageRef = useRef<HTMLDivElement | null>(null);
  const [singleLine, setSingleLine] = useState(true);
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when the message content changes
  useLayoutEffect(() => {
    const el = messageRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
      const line = Number.isFinite(lineHeight) ? lineHeight : 20;
      setSingleLine(el.scrollHeight <= line * 1.5);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [title, children, action, actionPlacement]);

  const closeButton = onClose ? (
    <IconButton aria-label={closeText} size="small" onClick={onClose}>
      <X />
    </IconButton>
  ) : null;

  // A right-placed CTA is vertically centred against the message block (Figma: the action row
  // inherits the container's centre alignment), while the close ✕ stays pinned to the top of its
  // own full-height column. Wrap only the CTA so the two keep independent vertical alignment.
  const rightAction =
    actionPlacement === 'right' && action ? (
      <Box sx={{ display: 'flex', alignItems: 'center', alignSelf: 'stretch' }}>{action}</Box>
    ) : null;

  // MUI renders its own close button only when `action` is unset; we always compose the close
  // button ourselves so it can coexist with a right-placed CTA. `undefined` (never an empty
  // fragment) when there is nothing to show — a truthy empty slot makes MuiAlert render an empty
  // `.MuiAlert-action` container (stray padding).
  const rightSlot =
    rightAction || closeButton ? (
      <>
        {rightAction}
        {closeButton}
      </>
    ) : undefined;

  return (
    <MuiAlert
      ref={ref}
      severity={severity}
      iconMapping={{ ...DEFAULT_ICON_MAPPING, ...iconMapping }}
      action={rightSlot}
      data-single-line={singleLine ? 'true' : undefined}
      {...rest}
      slotProps={{
        ...slotProps,
        message: { ...slotProps?.message, ref: messageRef },
      }}
    >
      {title ? <AlertTitle>{title}</AlertTitle> : null}
      {/* The description is wrapped in a block of its own. `.MuiAlert-message` is a flex COLUMN
          (its items are the title, this description and an optional bottom CTA), and a flex
          container makes EVERY child a flex item — including each inline element and each run of
          bare text between them. Without this wrapper a sentence like
          `Drop <strong>x.csv</strong> into <code>Y/</code>` renders as five stacked lines. The
          wrapper is one flex item from the outside and normal text flow on the inside. */}
      {isRenderable(children) ? <div className="OxAlert-description">{children}</div> : null}
      {/* Bottom CTA lives inside the message box, below the description (the theme spaces it with
          the message column's gap). */}
      {actionPlacement === 'bottom' ? action : null}
    </MuiAlert>
  );
});
